import { SimWorld, addLog, isGroupModeEnabled, isSameGroup } from '../types/SimWorld';
import { SimNPC } from '../types/SimNPC';
import { createFamilyRelation } from '../types/Family';
import { canBecomePartner, getMutualAffection } from './RelationshipManager';
import { POPULATION_CONSTANTS } from '../core/PopulationConstants';

const {
  ADULT_AGE,
  PARTNER_MIN_AFFECTION,
  PARTNER_STRENGTH_WEIGHT,
  PARTNER_SIMILARITY_WEIGHT,
  PARTNER_MIN_BATTLES,
} = POPULATION_CONSTANTS;

// マッチスコアの基本閾値（この値以上でパートナー成立）
const MATCH_SCORE_THRESHOLD_BASE = 50;
// 年齢による閾値緩和係数（年齢が1歳上がるごとにこの値だけ閾値が下がる）
const AGE_RELAXATION_RATE = 0.5;
// 閾値の最低値（これ以下にはならない）
const MATCH_SCORE_THRESHOLD_MIN = 20;

/**
 * 年齢に応じたマッチスコア閾値を計算
 * 年齢が上がるほど条件が緩くなる
 */
function getAgeBasedThreshold(age: number): number {
  const relaxation = (age - ADULT_AGE) * AGE_RELAXATION_RATE;
  return Math.max(MATCH_SCORE_THRESHOLD_MIN, MATCH_SCORE_THRESHOLD_BASE - relaxation);
}

/**
 * NPCの勝率を計算
 */
function getWinRate(npc: SimNPC): number {
  const totalBattles = npc.wins + npc.losses;
  if (totalBattles < PARTNER_MIN_BATTLES) return 0.5; // 戦闘数不足は中立
  return npc.wins / totalBattles;
}

/**
 * マッチスコア計算
 * 強さへの憧れ + 同レベル婚の複合スコア
 *
 * @param affection 好感度
 * @param myWinRate 自分の勝率
 * @param targetWinRate 相手の勝率
 * @returns マッチスコア
 */
function calculateMatchScore(
  affection: number,
  myWinRate: number,
  targetWinRate: number
): number {
  // 強さへの憧れ: 相手の勝率が高いほど魅力的
  const strengthBonus = 1 + targetWinRate * PARTNER_STRENGTH_WEIGHT;

  // 同レベル婚: 勝率差が大きいほどペナルティ
  const winRateDiff = Math.abs(myWinRate - targetWinRate);
  const similarityPenalty = 1 - winRateDiff * PARTNER_SIMILARITY_WEIGHT;

  // 最終スコア = 好感度 × 強さボーナス × 類似性ペナルティ
  return affection * strengthBonus * Math.max(0.5, similarityPenalty);
}

/**
 * パートナー形成処理
 * グループモードでは同グループ内でのみパートナーが形成される
 */
export function processPartnerFormation(world: SimWorld): void {
  const { npcs, day } = world;
  const groupModeEnabled = isGroupModeEnabled(world);

  // パートナー候補ペアとスコアを収集
  const candidates: Array<{
    npc1: SimNPC;
    npc2: SimNPC;
    score: number;
  }> = [];

  // 全NPCペアを検証
  for (let i = 0; i < npcs.length; i++) {
    for (let j = i + 1; j < npcs.length; j++) {
      const npc1 = npcs[i];
      const npc2 = npcs[j];

      // 既にパートナーがいる場合はスキップ
      if (npc1.hasPartner || npc2.hasPartner) continue;
      if (!npc1.isAlive || !npc2.isAlive) continue;

      // グループモードの場合は同グループのみ
      if (groupModeEnabled && !isSameGroup(npc1, npc2, world)) continue;

      // 成人チェック
      if (npc1.age < ADULT_AGE || npc2.age < ADULT_AGE) continue;

      // 基本条件チェック（好感度）
      if (!canBecomePartner(npc1, npc2, PARTNER_MIN_AFFECTION)) continue;

      // 勝率計算
      const winRate1 = getWinRate(npc1);
      const winRate2 = getWinRate(npc2);

      // 好感度取得
      const { aff1to2, aff2to1 } = getMutualAffection(npc1, npc2);

      // 双方向のマッチスコア計算
      const score1to2 = calculateMatchScore(aff1to2, winRate1, winRate2);
      const score2to1 = calculateMatchScore(aff2to1, winRate2, winRate1);

      // 年齢に応じた個別閾値（年配ほど条件が緩い）
      const threshold1 = getAgeBasedThreshold(npc1.age);
      const threshold2 = getAgeBasedThreshold(npc2.age);

      // 両方が自分の閾値以上ならペア成立候補
      if (score1to2 >= threshold1 && score2to1 >= threshold2) {
        // 平均スコアで優先度付け
        const avgScore = (score1to2 + score2to1) / 2;
        candidates.push({ npc1, npc2, score: avgScore });
      }
    }
  }

  // スコア順にソートして、高スコアペアから優先的にマッチング
  candidates.sort((a, b) => b.score - a.score);

  // マッチング（1人が複数とマッチしないように）
  for (const { npc1, npc2, score } of candidates) {
    if (npc1.hasPartner || npc2.hasPartner) continue;
    formPartnership(npc1, npc2, day, world, score);
  }
}

/**
 * パートナー関係を確立
 */
function formPartnership(
  npc1: SimNPC,
  npc2: SimNPC,
  day: number,
  world: SimWorld,
  matchScore?: number
): void {
  // 家族関係追加
  npc1.family.push(createFamilyRelation(npc2.id, 'partner', day));
  npc2.family.push(createFamilyRelation(npc1.id, 'partner', day));

  npc1.hasPartner = true;
  npc2.hasPartner = true;

  const scoreText = matchScore ? ` (相性: ${matchScore.toFixed(0)})` : '';
  addLog(world, `💑 ${npc1.name}と${npc2.name}がパートナーになった！${scoreText}`);
}

/**
 * パートナーを取得
 * @param npc 対象NPC
 * @param npcs NPCリスト（npcMapがない場合に使用）
 * @param npcMap オプション: ID->NPCのMap（O(1)検索用）
 */
export function getPartner(
  npc: SimNPC,
  npcs: SimNPC[],
  npcMap?: Map<string, SimNPC>
): SimNPC | null {
  const partnerRelation = npc.family.find((f) => f.relationType === 'partner');
  if (!partnerRelation) return null;

  // npcMapがあればO(1)、なければO(n)で検索
  if (npcMap) {
    return npcMap.get(partnerRelation.relationId) || null;
  }
  return npcs.find((n) => n.id === partnerRelation.relationId) || null;
}
